import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Icon } from '@iconify/react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../services/api';
import AddCommissionCompanyModal from './AddCommissionCompanyModal';
import EditCommissionCompanyModal from './EditCommissionCompanyModal';
import ViewCommissionCompanyModal from './ViewCommissionCompanyModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';

const CommissionCompaniesTable = () => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [currentCompany, setCurrentCompany] = useState(null);
    const [deletedCompany, setDeletedCompany] = useState(null);

    const fetchCompanies = async () => {
        setIsLoading(true);
        try {
            const response = await api.get('/commission-companies/unpaginated');
            if (response.data.status) {
                // Handle both response structures: response.data or just response (array)
                const companies = response.data.response?.data || response.data.response || [];
                setData(Array.isArray(companies) ? companies : []);
            } else {
                toast.error(response.data.message || 'Failed to fetch commission companies');
            }
        } catch (error) {
            console.error('Error fetching commission companies:', error);
            toast.error(error.response?.data?.message || 'Error fetching commission companies');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const handleView = (company) => {
        setCurrentCompany(company);
        setShowViewModal(true);
    };

    const handleEdit = (company) => {
        setCurrentCompany(company);
        setShowEditModal(true);
    };

    const handleDeleteClick = (company) => {
        setDeletedCompany(company);
        setShowDeleteConfirmationModal(true);
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/commission-companies/${deletedCompany.id}`);
            toast.success('Commission company deleted successfully!');
            fetchCompanies();
        } catch (error) {
            toast.error('Error deleting commission company.');
            console.error(error);
        } finally {
            setShowDeleteConfirmationModal(false);
            setDeletedCompany(null);
        }
    };

    const handleSaveAdd = () => {
        fetchCompanies();
        setShowAddModal(false);
    };

    const handleSaveEdit = () => {
        fetchCompanies();
        setShowEditModal(false);
        setCurrentCompany(null);
    };

    const columns = [
        {
            name: 'ID',
            selector: (row) => row.id,
            sortable: true,
            width: '80px',
        },
        {
            name: 'Name',
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="d-flex">
                    <button
                        onClick={() => handleView(row)}
                        className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                        title="View Details"
                    >
                        <Icon icon="iconamoon:eye-light" />
                    </button>
                    <button
                        onClick={() => handleEdit(row)}
                        className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                        title="Edit Company"
                    >
                        <Icon icon="lucide:edit" />
                    </button>
                    <button
                        onClick={() => handleDeleteClick(row)}
                        className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                        title="Delete Company"
                    >
                        <Icon icon="mingcute:delete-2-line" />
                    </button>
                </div>
            ),
            width: '150px',
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const customStyles = {
        rows: {
            style: {
                fontSize: '15px',
            },
        },
    };

    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Commission Companies</h5>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowAddModal(true)}
                    className="d-flex align-items-center gap-2"
                >
                    <Icon icon="ic:baseline-plus" className="icon text-sm" />
                    Add New Company
                </Button>
            </div>

            {/* Data Table */}
            <DataTable
                columns={columns}
                data={data}
                theme="customTheme"
                customStyles={customStyles}
                progressPending={isLoading}
                pagination
                highlightOnHover
                responsive
                striped
                noDataComponent="No commission companies found"
            />

            {/* Add Modal */}
            <AddCommissionCompanyModal
                show={showAddModal}
                handleClose={() => setShowAddModal(false)}
                handleSave={handleSaveAdd}
            />

            {/* Edit Modal */}
            <EditCommissionCompanyModal
                show={showEditModal}
                handleClose={() => {
                    setShowEditModal(false);
                    setCurrentCompany(null);
                }}
                company={currentCompany}
                handleSave={handleSaveEdit}
            />

            {/* View Modal */}
            <ViewCommissionCompanyModal
                show={showViewModal}
                handleClose={() => {
                    setShowViewModal(false);
                    setCurrentCompany(null);
                }}
                company={currentCompany}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                show={showDeleteConfirmationModal}
                handleClose={() => {
                    setShowDeleteConfirmationModal(false);
                    setDeletedCompany(null);
                }}
                handleDelete={handleDelete}
                content={
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>Delete Commission Company</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Are you sure you want to delete "{deletedCompany?.name || 'this company'}"? This action cannot be undone.
                        </Modal.Body>
                    </>
                }
            />
        </div>
    );
};

export default CommissionCompaniesTable;
